import ContactAPI from 'dashboard/api/contacts';

const CONTACT_DETAILS_TTL_MS = 60 * 1000;
const CONTACT_INBOXES_TTL_MS = 60 * 1000;
const CONTACT_LIST_TTL_MS = 60 * 1000;

const inflightContactRequests = new Map();
const inflightInboxRequests = new Map();
const inflightContactListRequests = new Map();
const contactFetchedAt = new Map();
const inboxesFetchedAt = new Map();
const contactListFetchedAt = new Map();

const normalizeContactId = contactId => {
  const parsed = Number(contactId);
  return Number.isNaN(parsed) || parsed <= 0 ? null : parsed;
};

const hasRenderableContactDetails = contact => {
  if (!contact?.id) return false;
  return Boolean(contact.name || contact.email || contact.phone_number);
};

const getCachedContact = (store, contactId) => {
  const getter = store?.getters?.['contacts/getContact'];
  if (typeof getter !== 'function') return null;
  const contact = getter(contactId);
  if (!contact || !contact.id) return null;
  return contact;
};

const isFresh = (fetchedAt, ttlMs) => {
  if (!fetchedAt) return false;
  return Date.now() - fetchedAt < ttlMs;
};

const normalizeAccountId = (store, accountId) => {
  const explicit = Number(accountId);
  if (!Number.isNaN(explicit) && explicit > 0) return explicit;
  const fromGetter = Number(store?.getters?.getCurrentAccountId);
  if (!Number.isNaN(fromGetter) && fromGetter > 0) return fromGetter;
  return 'default';
};

export const fetchContactDetailsWithCache = async (
  store,
  contactId,
  { force = false } = {}
) => {
  const normalizedContactId = normalizeContactId(contactId);
  if (!normalizedContactId) return null;

  const cachedContact = getCachedContact(store, normalizedContactId);
  const fetchedAt = contactFetchedAt.get(normalizedContactId);
  const shouldUseCache =
    !force &&
    cachedContact &&
    hasRenderableContactDetails(cachedContact) &&
    isFresh(fetchedAt, CONTACT_DETAILS_TTL_MS);

  if (shouldUseCache) return cachedContact;

  const inflight = inflightContactRequests.get(normalizedContactId);
  if (inflight) return inflight;

  const request = store
    .dispatch('contacts/show', { id: normalizedContactId })
    .catch(() => null)
    .finally(() => {
      inflightContactRequests.delete(normalizedContactId);
      contactFetchedAt.set(normalizedContactId, Date.now());
    });

  inflightContactRequests.set(normalizedContactId, request);
  await request;
  return getCachedContact(store, normalizedContactId);
};

export const fetchContactInboxesWithCache = async (
  store,
  contactId,
  { force = false } = {}
) => {
  const normalizedContactId = normalizeContactId(contactId);
  if (!normalizedContactId) return null;

  const cachedContact = getCachedContact(store, normalizedContactId);
  const hasInboxes =
    Array.isArray(cachedContact?.contact_inboxes) &&
    cachedContact.contact_inboxes.length >= 0;
  const fetchedAt = inboxesFetchedAt.get(normalizedContactId);
  const shouldUseCache =
    !force && hasInboxes && isFresh(fetchedAt, CONTACT_INBOXES_TTL_MS);

  if (shouldUseCache) return cachedContact.contact_inboxes;

  const inflight = inflightInboxRequests.get(normalizedContactId);
  if (inflight) return inflight;

  const request = store
    .dispatch('contacts/fetchContactableInbox', normalizedContactId)
    .catch(() => null)
    .finally(() => {
      inflightInboxRequests.delete(normalizedContactId);
      inboxesFetchedAt.set(normalizedContactId, Date.now());
    });

  inflightInboxRequests.set(normalizedContactId, request);
  return request;
};

export const fetchContactsListWithCache = async (
  store,
  { accountId = null, force = false } = {}
) => {
  const scopedAccountId = normalizeAccountId(store, accountId);
  const contacts = store?.getters?.['contacts/getContacts'] || [];
  const fetchedAt = contactListFetchedAt.get(scopedAccountId);
  const shouldUseCache =
    !force && Array.isArray(contacts) && contacts.length > 0 && isFresh(fetchedAt, CONTACT_LIST_TTL_MS);

  if (shouldUseCache) return contacts;

  const inflight = inflightContactListRequests.get(scopedAccountId);
  if (inflight) return inflight;

  const request = ContactAPI.get(1)
    .then(({ data }) => {
      const payload = data?.payload || [];
      if (payload.length) {
        store.commit('contacts/APPEND_CONTACTS', payload);
      }
    })
    .catch(() => null)
    .finally(() => {
      inflightContactListRequests.delete(scopedAccountId);
      contactListFetchedAt.set(scopedAccountId, Date.now());
    });

  inflightContactListRequests.set(scopedAccountId, request);
  await request;
  return store?.getters?.['contacts/getContacts'] || [];
};
